import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { PromotionsBanner } from '@/components/home';
import { doctorService, clinicService, specialtyService, transformDoctor, transformSpecialty, getLocalizedValue, normalizeImageValue } from '@/services/api';
import { DEFAULT_CLINIC_IMAGE, getDefaultDoctorImage, handleImageFallback } from '@/lib/defaultImages';
import { isDoctorAccount } from '@/lib/userRoles';
import { useAuth } from '@/contexts/AuthContext';
import { 
  Search, 
  Calendar, 
  Shield, 
  Clock, 
  Star,
  Heart,
  Eye,
  Brain,
  Bone,
  Baby,
  Stethoscope,
  ArrowRight,
  CheckCircle2,
  MapPin,
  Phone,
  Hospital
} from 'lucide-react';

const getIconForSpecialty = (name) => {
  const nameLower = (name || '').toLowerCase();
  if (nameLower.includes('قلب') || nameLower.includes('cardio') || nameLower.includes('heart')) return Heart;
  if (nameLower.includes('عيون') || nameLower.includes('ophthal') || nameLower.includes('eye')) return Eye;
  if (nameLower.includes('عصب') || nameLower.includes('neuro') || nameLower.includes('brain')) return Brain;
  if (nameLower.includes('عظم') || nameLower.includes('ortho') || nameLower.includes('bone')) return Bone;
  if (nameLower.includes('أطفال') || nameLower.includes('pediatr') || nameLower.includes('child')) return Baby;
  return Stethoscope;
};

const getColorForSpecialty = (name) => {
  const nameLower = (name || '').toLowerCase();
  if (nameLower.includes('قلب') || nameLower.includes('cardio')) return 'bg-red-50 text-red-600';
  if (nameLower.includes('عيون') || nameLower.includes('ophthal')) return 'bg-blue-50 text-blue-600';
  if (nameLower.includes('عصب') || nameLower.includes('neuro')) return 'bg-purple-50 text-purple-600';
  if (nameLower.includes('عظم') || nameLower.includes('ortho')) return 'bg-orange-50 text-orange-600';
  if (nameLower.includes('أطفال') || nameLower.includes('pediatr')) return 'bg-pink-50 text-pink-600';
  return 'bg-teal-50 text-teal-600';
};

const getClinicImage = (clinic) =>
  normalizeImageValue(
    clinic.image ||
    clinic.image_url ||
    clinic.media?.[0]?.url ||
    clinic.media?.[0]?.thumb ||
    clinic.media?.[0]?.icon ||
    ''
  ) || DEFAULT_CLINIC_IMAGE;

const HomePage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const lang = i18n.language || 'ar';
  const isDoctorUser = isDoctorAccount(user);
  
  // State for API data
  const [doctors, setDoctors] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Handle search
  const handleSearch = () => {
    if (searchQuery.trim()) {
      navigate(`/doctors?search=${encodeURIComponent(searchQuery.trim())}`);
    } else {
      navigate('/doctors');
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch doctors and clinics in parallel from Odoo API
        const [doctorsRes, clinicsRes, specialtiesRes] = await Promise.all([
          doctorService.getAllDoctors(),
          clinicService.getAllClinics(),
          specialtyService.getAll(),
        ]);
        
        // Process doctors
        const rawDoctors =
          doctorsRes.data?.doctors ||
          doctorsRes.data?.data?.doctors ||
          doctorsRes.data?.data ||
          doctorsRes.data ||
          [];
        if (Array.isArray(rawDoctors) && rawDoctors.length > 0) {
          setDoctors(rawDoctors.map(d => transformDoctor(d, lang)));
        }
        
        // Process clinics - Handle Odoo response structure
        const rawClinics = clinicsRes.data?.clinics || clinicsRes.data?.data?.clinics || [];
        if (Array.isArray(rawClinics)) {
          setClinics(rawClinics);
        }

        const rawSpecialties =
          specialtiesRes.data?.data?.specialities ||
          specialtiesRes.data?.specialities ||
          specialtiesRes.data?.data ||
          specialtiesRes.data ||
          [];
        if (Array.isArray(rawSpecialties) && rawSpecialties.length > 0) {
          setSpecialties(rawSpecialties.map((item) => transformSpecialty(item, lang)));
        } else {
          setSpecialties([]);
        }
        
        // Hospitals endpoint doesn't exist yet - skip for now
        // TODO: Create /api/v1/hospitals endpoint in Odoo if needed
      } catch (error) {
        console.error('Failed to fetch home page data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [lang]);

  const specialtiesToDisplay = specialties
    .slice(0, 6)
    .map((specialty) => ({
        id: specialty.id,
        name: specialty.name,
        count: specialty.doctors_count || 0,
        image: specialty.image || '',
        icon: getIconForSpecialty(specialty.name),
        colorClass: getColorForSpecialty(specialty.name),
        colorValue: specialty.color || '',
      }));

  const steps = [
    { icon: Search, title: t('home.step1Title'), description: t('home.step1Desc') },
    { icon: Calendar, title: t('home.step2Title'), description: t('home.step2Desc') },
    { icon: CheckCircle2, title: t('home.step3Title'), description: t('home.step3Desc') },
  ];

  const features = [
    { icon: Shield, title: t('home.feature1Title'), description: t('home.feature1Desc') },
    { icon: Calendar, title: t('home.feature2Title'), description: t('home.feature2Desc') },
    { icon: Clock, title: t('home.feature3Title'), description: t('home.feature3Desc') },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-600 via-teal-700 to-slate-800 text-white overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djZoNnYtNmgtNnptMC0xMHY2aDZ2LTZoLTZ6bTAtMTB2Nmg2di02aC02em0xMCAxMHY2aDZ2LTZoLTZ6bTAgMTB2Nmg2di02aC02em0wIDEwdjZoNnYtNmgtNnptLTIwLTIwdjZoNnYtNmgtNnptMCAxMHY2aDZ2LTZoLTZ6bTAgMTB2Nmg2di02aC02eiIvPjwvZz48L2c+PC9zdmc+')] opacity-20"></div>
        <div className="container mx-auto px-4 py-20 md:py-28 relative">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="mb-4 bg-white/10 text-white border-white/20 hover:bg-white/20" data-testid="hero-badge">
              ✨ Trusted by 50,000+ patients
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight" data-testid="hero-title">
              {t('home.heroTitle')}
            </h1>
            <p className="text-lg md:text-xl text-teal-100 mb-8 max-w-2xl mx-auto" data-testid="hero-subtitle">
              {t('home.heroSubtitle')}
            </p>
            
            {/* Search Box */}
            <div className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto" data-testid="hero-search">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input 
                  placeholder={t('home.searchPlaceholder')} 
                  className="pl-10 h-12 bg-white text-slate-900 border-0 focus-visible:ring-2 focus-visible:ring-teal-400"
                  data-testid="search-input"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleSearchKeyPress}
                />
              </div>
              <Button 
                size="lg" 
                className="h-12 bg-slate-900 hover:bg-slate-800 text-white" 
                data-testid="search-button"
                onClick={handleSearch}
              >
                <Search className="h-5 w-5 mr-2" />
                {t('common.search')}
              </Button>
            </div>
          </div>
        </div>
        
        {/* Wave Decoration */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="w-full h-12 md:h-16" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V95.8C57.1,118.92,150.72,85.52,213.19,76.28,276.7,66.84,262.91,67.49,321.39,56.44Z" fill="#f8fafc"></path>
          </svg>
        </div>
      </section>

      {/* Promotions Banner */}
      <section className="py-8 bg-slate-50 dark:bg-slate-900" data-testid="promotions-section">
        <div className="container mx-auto px-4">
          <PromotionsBanner />
        </div>
      </section>

      {/* Specialties Section */}
      <section className="py-16 md:py-20 bg-slate-50 dark:bg-slate-900" data-testid="specialties-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">{t('home.topSpecialties')}</h2>
            <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">{t('home.topSpecialtiesSubtitle')}</p>
          </div>
          
          {specialtiesToDisplay.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {specialtiesToDisplay.map((specialty) => (
                <Link 
                  key={specialty.id} 
                  to={`/doctors?specialty=${specialty.id}`}
                  className="group"
                  data-testid={`specialty-card-${specialty.id}`}
                >
                  <Card className="h-full border-0 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 bg-white dark:bg-slate-800 dark:border-slate-700">
                    <CardContent className="p-6 text-center">
                      <div
                        className={`w-14 h-14 mx-auto rounded-xl ${specialty.colorClass || specialty.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform overflow-hidden`}
                        style={specialty.colorValue ? {
                          backgroundColor: `${specialty.colorValue}22`,
                          color: specialty.colorValue,
                        } : undefined}
                      >
                        {specialty.image ? (
                          <img
                            src={specialty.image}
                            alt={specialty.name}
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <specialty.icon className="h-7 w-7" />
                        )}
                      </div>
                      <h3 className="font-semibold text-slate-900 dark:text-white mb-1">{specialty.name}</h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400">{specialty.count} {t('nav.doctors')}</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500 dark:text-slate-400">
              {lang === 'ar' ? 'لا توجد تخصصات متاحة حالياً' : 'No specialties available at the moment'}
            </div>
          )}
        </div>
      </section>

      {/* Doctors Section - From API */}
      <section className="py-16 md:py-20 bg-white dark:bg-slate-800" data-testid="doctors-section">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-2">
                {lang === 'ar' ? 'الأطباء' : 'Doctors'}
              </h2>
              <p className="text-slate-600 dark:text-slate-400 dark:text-slate-500 dark:text-slate-400">
                {lang === 'ar' ? 'أفضل الأطباء المتخصصين' : 'Top specialized doctors'}
              </p>
            </div>
            <Button variant="outline" asChild className="hidden sm:flex" data-testid="view-all-doctors">
              <Link to="/doctors">
                {lang === 'ar' ? 'عرض الكل' : 'View All'} <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </Button>
          </div>
          
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <Skeleton className="w-20 h-20 rounded-xl" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-5 w-3/4" />
                        <Skeleton className="h-4 w-1/2" />
                        <Skeleton className="h-4 w-2/3" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : doctors.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {doctors.slice(0, 6).map((doctor) => (
                <Card key={doctor.id} className="dark:bg-slate-800 border-0 shadow-sm hover:shadow-lg transition-all duration-300" data-testid={`doctor-card-${doctor.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <img 
                        src={doctor.image || getDefaultDoctorImage(doctor.gender)}
                        alt={doctor.name}
                        className="w-20 h-20 rounded-xl object-cover"
                        onError={handleImageFallback(getDefaultDoctorImage(doctor.gender))}
                      />
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-900 dark:text-white mb-1">{doctor.name}</h3>
                        {doctor.address && (
                          <p className="text-sm text-teal-600 mb-2">{doctor.address}</p>
                        )}
                        <div className="flex items-center gap-2 text-sm">
                          <div className="flex items-center text-amber-500">
                            <Star className="h-4 w-4 fill-current" />
                            <span className="ml-1 font-medium">{doctor.rating || 0}</span>
                          </div>
                          <span className="text-slate-400 dark:text-slate-500 dark:text-slate-400">({doctor.reviews_count || 0} {t('doctors.reviews')})</span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-slate-100 flex justify-between items-center">
                      {doctor.experience_years > 0 && (
                        <span className="text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-400">
                          {doctor.experience_years} {t('doctors.years')}
                        </span>
                      )}
                      {!isDoctorUser && (
                        <Button size="sm" className="bg-teal-600 hover:bg-teal-700" asChild>
                          <Link to={`/doctors/${doctor.id}`}>{t('doctors.bookNow')}</Link>
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-400">
              {lang === 'ar' ? 'لا يوجد أطباء متاحين حالياً' : 'No doctors available at the moment'}
            </div>
          )}
          
          <div className="mt-8 text-center sm:hidden">
            <Button variant="outline" asChild>
              <Link to="/doctors">{lang === 'ar' ? 'عرض جميع الأطباء' : 'View All Doctors'} <ArrowRight className="h-4 w-4 ml-2" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Clinics Section - From API */}
      <section className="py-16 md:py-20 bg-slate-50 dark:bg-slate-900" data-testid="clinics-section">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-2">
                {lang === 'ar' ? 'العيادات' : 'Clinics'}
              </h2>
              <p className="text-slate-600 dark:text-slate-400 dark:text-slate-500 dark:text-slate-400">
                {lang === 'ar' ? 'أفضل العيادات والمراكز الطبية' : 'Top clinics and medical centers'}
              </p>
            </div>
            <Button variant="outline" asChild className="hidden sm:flex" data-testid="view-all-clinics">
              <Link to="/clinics">
                {lang === 'ar' ? 'عرض الكل' : 'View All'} <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </Button>
          </div>
          
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2].map((i) => (
                <Card key={i} className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <Skeleton className="w-20 h-20 rounded-xl" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-5 w-3/4" />
                        <Skeleton className="h-4 w-1/2" />
                        <Skeleton className="h-4 w-2/3" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : clinics.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {clinics.slice(0, 6).map((clinic) => (
                <Card key={clinic.id} className="dark:bg-slate-800 border-0 shadow-sm hover:shadow-lg transition-all duration-300" data-testid={`clinic-card-${clinic.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-20 h-20 rounded-xl bg-white flex items-center justify-center overflow-hidden">
                        <img
                          src={getClinicImage(clinic)}
                          alt={getLocalizedValue(clinic.name, lang)}
                          className="w-full h-full object-contain"
                          onError={handleImageFallback(DEFAULT_CLINIC_IMAGE)}
                        />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-900 dark:text-white mb-1">
                          {getLocalizedValue(clinic.name, lang)}
                        </h3>
                        {clinic.address && (
                          <div className="flex items-center gap-1 text-sm text-slate-600 mb-2">
                            <MapPin className="h-3.5 w-3.5" />
                            <span>{clinic.address.address || clinic.address.description}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-sm">
                          <div className="flex items-center text-amber-500">
                            <Star className="h-4 w-4 fill-current" />
                            <span className="ml-1 font-medium">{clinic.rate || 0}</span>
                          </div>
                          <span className="text-slate-400 dark:text-slate-500 dark:text-slate-400">({clinic.total_appointments || 0} {lang === 'ar' ? 'حجز' : 'bookings'})</span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-slate-100 flex justify-between items-center">
                      {clinic.phone_number && (
                        <div className="flex items-center gap-1 text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-400">
                          <Phone className="h-3.5 w-3.5" />
                          <span>{clinic.phone_number}</span>
                        </div>
                      )}
                      <Button size="sm" className="bg-teal-600 hover:bg-teal-700" asChild>
                        <Link to={`/clinics/${clinic.id}`}>
                          {lang === 'ar' ? 'عرض التفاصيل' : 'View Details'}
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-400">
              {lang === 'ar' ? 'لا توجد عيادات متاحة حالياً' : 'No clinics available at the moment'}
            </div>
          )}
        </div>
      </section>

      {/* Hospitals Section - From API (if available) */}
      {hospitals.length > 0 && (
        <section className="py-16 md:py-20 bg-white dark:bg-slate-800" data-testid="hospitals-section">
          <div className="container mx-auto px-4">
            <div className="flex justify-between items-center mb-8">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-2">
                  {lang === 'ar' ? 'المستشفيات' : 'Hospitals'}
                </h2>
                <p className="text-slate-600 dark:text-slate-400 dark:text-slate-500 dark:text-slate-400">
                  {lang === 'ar' ? 'أفضل المستشفيات والمراكز الطبية' : 'Top hospitals and medical centers'}
                </p>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {hospitals.slice(0, 6).map((hospital) => (
                <Card key={hospital.id} className="dark:bg-slate-800 border-0 shadow-sm hover:shadow-lg transition-all duration-300" data-testid={`hospital-card-${hospital.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-20 h-20 rounded-xl bg-blue-100 flex items-center justify-center">
                        <Hospital className="w-10 h-10 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-900 dark:text-white mb-1">
                          {getLocalizedValue(hospital.name, lang)}
                        </h3>
                        {hospital.address && (
                          <div className="flex items-center gap-1 text-sm text-slate-600 mb-2">
                            <MapPin className="h-3.5 w-3.5" />
                            <span>{hospital.address.address || hospital.address.description}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* How It Works Section */}
      <section className="py-16 md:py-20 bg-slate-50 dark:bg-slate-900" data-testid="how-it-works-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">{t('home.howItWorks')}</h2>
            <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">{t('home.howItWorksSubtitle')}</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {steps.map((step, index) => (
              <div key={index} className="text-center" data-testid={`step-${index + 1}`}>
                <div className="relative inline-flex">
                  <div className="w-20 h-20 rounded-2xl bg-teal-600 flex items-center justify-center text-white mb-6 mx-auto">
                    <step.icon className="h-10 w-10" />
                  </div>
                  {index < steps.length - 1 && (
                    <div className="hidden md:block absolute top-1/2 left-full w-full h-0.5 bg-teal-200 -translate-y-1/2"></div>
                  )}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">{step.title}</h3>
                <p className="text-slate-600 dark:text-slate-400 dark:text-slate-500 dark:text-slate-400">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 md:py-20 bg-white dark:bg-slate-800" data-testid="why-choose-us-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">{t('home.whyChooseUs')}</h2>
            <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">{t('home.whyChooseUsSubtitle')}</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="dark:bg-slate-800 border-0 shadow-sm bg-slate-50 dark:bg-slate-900" data-testid={`feature-${index + 1}`}>
                <CardContent className="p-8">
                  <div className="w-14 h-14 rounded-xl bg-teal-100 text-teal-600 flex items-center justify-center mb-6">
                    <feature.icon className="h-7 w-7" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">{feature.title}</h3>
                  <p className="text-slate-600 dark:text-slate-400 dark:text-slate-500 dark:text-slate-400">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-teal-600 to-teal-700 text-white" data-testid="cta-section">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('home.ctaTitle')}</h2>
          <p className="text-teal-100 mb-8 max-w-2xl mx-auto">
            {t('home.ctaSubtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-teal-700 hover:bg-teal-50" asChild data-testid="cta-get-started">
              <Link to="/register">{t('home.ctaButton')}</Link>
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10" asChild data-testid="cta-browse-doctors">
              <Link to="/doctors">{t('nav.doctors')}</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
